import React, { useState, useEffect, useMemo, useCallback } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import styles from './DealsRegister.module.css';
import { getAllDeals, recordPayment, setDealTaxInclusive } from '../api/customerApi';
import { toast } from 'react-toastify';
import { formatDateDDMMYYYY } from '../utils/dateFormat';

const translations = {
  en: {
    pageTitle: 'Invoices & Payments',
    homepage: 'Homepage',
    loading: 'Loading deals…',
    errorLoad: 'Failed to load deals.',
    noDeals: 'No deals found.',
    noDealsFiltered: 'No deals match the current filters.',
    // Summary cards
    totalInvoiced: 'Total Invoiced',
    totalCollected: 'Total Collected',
    totalOutstanding: 'Total Outstanding',
    overduePostpaid: 'Overdue Postpaid',
    beforeTax: 'Before Tax',
    withTax: 'With Tax',
    // Filters
    filterByStatus: 'Payment Status',
    period: 'Period',
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    yearly: 'Yearly',
    filterFrom: 'From',
    filterTo: 'To',
    searchCustomer: 'Search customer…',
    allStatuses: 'All Statuses',
    clearFilters: 'Clear',
    showOverdueOnly: 'Overdue only',
    // Table headers
    customer: 'Customer',
    salesRep: 'Sales Rep',
    date: 'Date',
    subtotal: 'Subtotal',
    discount: 'Discount',
    beforeTaxShort: 'Before Tax',
    tax: 'Tax',
    afterTax: 'Total (w/ Tax)',
    paid: 'Paid',
    remaining: 'Remaining',
    status: 'Status',
    invoiceType: 'Invoice Type',
    dueDate: 'Due Date',
    actions: 'Actions',
    // Status labels
    unpaid: 'Unpaid',
    partiallyPaid: 'Partial',
    paidLabel: 'Paid',
    // Tax toggle
    collectWithTax: 'Collect with tax',
    collectWithoutTax: 'Collect without tax',
    taxLocked: 'Tax mode locked after first payment',
    // Payment modal
    recordPayment: 'Record Payment',
    amount: 'Amount',
    method: 'Payment Method',
    notes: 'Notes (optional)',
    cash: 'Cash',
    postpaid: 'Postpaid',
    bankTransfer: 'Bank Transfer',
    cheque: 'Cheque',
    other: 'Other',
    amountDueLabel: 'Amount Due',
    remainingLabel: 'Remaining Balance',
    confirm: 'Confirm',
    cancel: 'Cancel',
    paymentSuccess: 'Payment recorded.',
    paymentError: 'Failed to record payment.',
    taxToggleError: 'Failed to update tax mode.',
    // Payment history
    paymentHistory: 'Payment History',
    noPayments: 'No payments recorded yet.',
    historyDate: 'Date',
    historyAmount: 'Amount',
    historyMethod: 'Method',
    historyBy: 'Recorded By',
    historyNotes: 'Notes',
    products: 'Products',
    product: 'Product',
    sort: 'Sort',
    color: 'Color',
    quantity: 'Qty',
    unitPrice: 'Unit Price',
    lineTotal: 'Line Total',
    showHistory: 'History',
    hideHistory: 'Hide',
  },
  ar: {
    pageTitle: 'الفواتير والمدفوعات',
    homepage: 'الرئيسية',
    loading: 'جارٍ تحميل الصفقات…',
    errorLoad: 'فشل تحميل الصفقات.',
    noDeals: 'لا توجد صفقات.',
    noDealsFiltered: 'لا توجد صفقات تطابق الفلاتر المحددة.',
    totalInvoiced: 'إجمالي الفواتير',
    totalCollected: 'إجمالي المحصّل',
    totalOutstanding: 'إجمالي المتبقي',
    overduePostpaid: 'فواتير آجلة متأخرة',
    beforeTax: 'قبل الضريبة',
    withTax: 'شامل الضريبة',
    filterByStatus: 'حالة الدفع',
    period: 'الفترة',
    daily: 'يومي',
    weekly: 'أسبوعي',
    monthly: 'شهري',
    yearly: 'سنوي',
    filterFrom: 'من',
    filterTo: 'إلى',
    searchCustomer: 'بحث عن عميل…',
    allStatuses: 'جميع الحالات',
    clearFilters: 'مسح',
    showOverdueOnly: 'المتأخر فقط',
    customer: 'العميل',
    salesRep: 'مندوب المبيعات',
    date: 'التاريخ',
    subtotal: 'المجموع الفرعي',
    discount: 'الخصم',
    beforeTaxShort: 'قبل الضريبة',
    tax: 'الضريبة',
    afterTax: 'الإجمالي (شامل الضريبة)',
    paid: 'المدفوع',
    remaining: 'المتبقي',
    status: 'الحالة',
    invoiceType: 'نوع الفاتورة',
    dueDate: 'تاريخ الاستحقاق',
    actions: 'الإجراءات',
    unpaid: 'غير مدفوع',
    partiallyPaid: 'جزئي',
    paidLabel: 'مدفوع',
    collectWithTax: 'التحصيل شامل الضريبة',
    collectWithoutTax: 'التحصيل بدون ضريبة',
    taxLocked: 'وضع الضريبة مقفل بعد أول دفعة',
    recordPayment: 'تسجيل دفعة',
    amount: 'المبلغ',
    method: 'طريقة الدفع',
    notes: 'ملاحظات (اختياري)',
    cash: 'نقداً',
    postpaid: 'آجل',
    bankTransfer: 'تحويل بنكي',
    cheque: 'شيك',
    other: 'أخرى',
    amountDueLabel: 'المبلغ المستحق',
    remainingLabel: 'الرصيد المتبقي',
    confirm: 'تأكيد',
    cancel: 'إلغاء',
    paymentSuccess: 'تم تسجيل الدفعة.',
    paymentError: 'فشل تسجيل الدفعة.',
    taxToggleError: 'فشل تحديث وضع الضريبة.',
    paymentHistory: 'سجل المدفوعات',
    noPayments: 'لم تُسجَّل أي دفعات بعد.',
    historyDate: 'التاريخ',
    historyAmount: 'المبلغ',
    historyMethod: 'الطريقة',
    historyBy: 'سجّله',
    historyNotes: 'ملاحظات',
    products: 'المنتجات',
    product: 'المنتج',
    sort: 'الصنف',
    color: 'اللون',
    quantity: 'الكمية',
    unitPrice: 'سعر الوحدة',
    lineTotal: 'الإجمالي',
    showHistory: 'السجل',
    hideHistory: 'إخفاء',
  },
};

const fmt = (n) =>
  Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const fmtDate = (d) => {
  return formatDateDDMMYYYY(d, '—');
};

const toInputDate = (date) => date.toISOString().split('T')[0];

const getPeriodRange = (period) => {
  const end = new Date();
  const start = new Date(end);
  if (period === 'daily') start.setDate(end.getDate() - 1);
  if (period === 'weekly') start.setDate(end.getDate() - 7);
  if (period === 'monthly') start.setMonth(end.getMonth() - 1);
  if (period === 'yearly') start.setFullYear(end.getFullYear() - 1);
  return {
    from: toInputDate(start),
    to: toInputDate(end),
  };
};

const paymentStatusClass = {
  unpaid: styles.badgeUnpaid,
  'partially-paid': styles.badgePartial,
  paid: styles.badgePaid,
};

const DealsRegister = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key] ?? key;
  const navigate = useNavigate();
  const userInfo = useMemo(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return { userType: null };
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return { userType: payload.userType };
    } catch {
      return { userType: null };
    }
  }, []);
  const canRecordPayments = ['accounting', 'admin'].includes(userInfo.userType);
  const canChangeTaxMode = ['accounting', 'admin', 'manager'].includes(userInfo.userType);

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('monthly');
  const initialRange = useMemo(() => getPeriodRange('monthly'), []);
  const [filterFrom, setFilterFrom] = useState(initialRange.from);
  const [filterTo, setFilterTo] = useState(initialRange.to);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterOverdueOnly, setFilterOverdueOnly] = useState(false);

  // Expanded payment history rows
  const [expanded, setExpanded] = useState(new Set());

  // Payment modal
  const [payModal, setPayModal] = useState(null); // { dealRow }
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('cash');
  const [payNotes, setPayNotes] = useState('');
  const [payLoading, setPayLoading] = useState(false);

  // Tax toggle loading
  const [taxToggling, setTaxToggling] = useState(null); // dealId being toggled

  const fetchDeals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await getAllDeals();
      setDeals(data);
    } catch {
      setError(t('errorLoad'));
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const getDealDate = useCallback((deal) => deal?.dealDate || deal?.createdAt, []);

  const isOverduePostpaid = useCallback((deal) => {
    if (deal?.paymentType !== 'postpaid' || deal?.paymentStatus === 'paid' || !deal?.paymentDueDate) {
      return false;
    }
    const dueDate = new Date(deal.paymentDueDate);
    if (Number.isNaN(dueDate.getTime())) return false;
    dueDate.setHours(23, 59, 59, 999);
    return dueDate < new Date();
  }, []);

  const filteredDeals = useMemo(() => {
    return deals.filter((row) => {
      const dealDate = getDealDate(row.deal);
      if (filterStatus && row.deal.paymentStatus !== filterStatus) return false;
      if (filterOverdueOnly && !isOverduePostpaid(row.deal)) return false;
      if (filterFrom && new Date(dealDate) < new Date(filterFrom)) return false;
      if (filterTo) {
        const to = new Date(filterTo);
        to.setHours(23, 59, 59, 999);
        if (new Date(dealDate) > to) return false;
      }
      if (filterCustomer) {
        const q = filterCustomer.toLowerCase();
        if (
          !row.customerName?.toLowerCase().includes(q) &&
          !row.customerID?.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [deals, filterStatus, filterFrom, filterTo, filterCustomer, filterOverdueOnly, getDealDate, isOverduePostpaid]);

  const summary = useMemo(() => {
    let invoicedBefore = 0, invoicedAfter = 0, collected = 0, overdue = 0;
    filteredDeals.forEach(({ deal }) => {
      invoicedBefore += deal.amountBeforeTax || 0;
      invoicedAfter  += deal.amountAfterTax  || 0;
      collected      += deal.paidAmount       || 0;
      if (isOverduePostpaid(deal)) overdue += 1;
    });
    return {
      invoicedBefore,
      invoicedAfter,
      collected,
      outstandingBefore: invoicedBefore - collected,
      outstandingAfter:  invoicedAfter  - collected,
      overdue,
    };
  }, [filteredDeals, isOverduePostpaid]);

  const hasFilters = filterStatus || filterFrom || filterTo || filterCustomer || filterOverdueOnly;

  const applyPeriod = (period) => {
    const range = getPeriodRange(period);
    setFilterPeriod(period);
    setFilterFrom(range.from);
    setFilterTo(range.to);
  };

  const clearFilters = () => {
    setFilterStatus('');
    setFilterPeriod('monthly');
    const range = getPeriodRange('monthly');
    setFilterFrom(range.from);
    setFilterTo(range.to);
    setFilterCustomer('');
    setFilterOverdueOnly(false);
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openPayModal = (row) => {
    setPayModal(row);
    setPayAmount('');
    setPayMethod('cash');
    setPayNotes('');
  };

  const closePayModal = () => { setPayModal(null); };

  const handleRecordPayment = async () => {
    if (!payModal) return;
    const amt = parseFloat(payAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid amount.');
      return;
    }
    setPayLoading(true);
    try {
      const { data: updatedDeal } = await recordPayment(
        payModal.customerId,
        payModal.deal._id,
        { amount: amt, method: payMethod, notes: payNotes }
      );
      setDeals((prev) =>
        prev.map((row) =>
          String(row.deal._id) === String(payModal.deal._id)
            ? { ...row, deal: { ...row.deal, ...updatedDeal } }
            : row
        )
      );
      toast.success(t('paymentSuccess'));
      closePayModal();
    } catch {
      toast.error(t('paymentError'));
    } finally {
      setPayLoading(false);
    }
  };

  const handleTaxToggle = async (row, newValue) => {
    setTaxToggling(String(row.deal._id));
    try {
      const { data: updatedDeal } = await setDealTaxInclusive(
        row.customerId,
        row.deal._id,
        newValue
      );
      setDeals((prev) =>
        prev.map((r) =>
          String(r.deal._id) === String(row.deal._id)
            ? { ...r, deal: { ...r.deal, ...updatedDeal } }
            : r
        )
      );
    } catch {
      toast.error(t('taxToggleError'));
    } finally {
      setTaxToggling(null);
    }
  };

  const getAmountDue = (deal) =>
    deal.taxInclusive ? (deal.amountAfterTax || 0) : (deal.amountBeforeTax || 0);

  const getRemaining = (deal) => Math.max(0, getAmountDue(deal) - (deal.paidAmount || 0));

  const getLineTotal = (product) => {
    const quantity = Number(product?.quantity) || 0;
    const unitPrice = Number(product?.pricePerItem) || 0;
    const pricePerQuantity = Number(product?.pricePerQuantity) || 0;
    const effectivePrice = pricePerQuantity > 0 ? pricePerQuantity : unitPrice;
    const base = quantity * effectivePrice;
    const discountPercent = Math.min(100, Math.max(0, Number(product?.discount) || 0));
    return base - (base * discountPercent / 100);
  };

  const methodLabel = (m) => {
    const map = { cash: t('cash'), bank_transfer: t('bankTransfer'), cheque: t('cheque'), other: t('other') };
    return map[m] || m;
  };

  const statusLabel = (s) => {
    const map = { unpaid: t('unpaid'), 'partially-paid': t('partiallyPaid'), paid: t('paidLabel') };
    return map[s] || s;
  };

  const modalDeal = payModal?.deal;
  const modalAmountDue = modalDeal ? getAmountDue(modalDeal) : 0;
  const modalRemaining = modalDeal ? getRemaining(modalDeal) : 0;

  return (
    <AppLayout
      title={t('pageTitle')}
      language={language}
      languageLabel={language === 'en' ? 'عربي' : 'English'}
      onToggleLanguage={() => setLanguage((p) => (p === 'en' ? 'ar' : 'en'))}
      showHome
      homeLabel={t('homepage')}
      onHome={() => navigate(userInfo.userType === 'accounting' ? '/accounting' : '/sales-manager')}
      contentClassName={styles.page}
    >
      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>{t('totalInvoiced')}</span>
          <span className={styles.summaryValue}>{fmt(summary.invoicedAfter)}</span>
          <span className={styles.summaryMeta}>{t('beforeTax')}: {fmt(summary.invoicedBefore)}</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>{t('totalCollected')}</span>
          <span className={`${styles.summaryValue} ${styles.summaryGreen}`}>{fmt(summary.collected)}</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>{t('totalOutstanding')}</span>
          <span className={`${styles.summaryValue} ${styles.summaryRed}`}>{fmt(summary.outstandingAfter)}</span>
          <span className={styles.summaryMeta}>{t('beforeTax')}: {fmt(summary.outstandingBefore)}</span>
        </div>
        <div className={styles.summaryCard}>
          <span className={styles.summaryLabel}>{t('overduePostpaid')}</span>
          <span className={`${styles.summaryValue} ${styles.summaryRed}`}>{summary.overdue}</span>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filtersBar}>
        <label className={styles.filterGroup}>
          <span className={styles.filterLabel}>{t('searchCustomer')}</span>
          <input
            className={styles.filterInput}
            type="text"
            placeholder={t('searchCustomer')}
            value={filterCustomer}
            onChange={(e) => setFilterCustomer(e.target.value)}
          />
        </label>
        <label className={styles.filterGroup}>
          <span className={styles.filterLabel}>{t('filterByStatus')}</span>
          <select
            className={styles.filterSelect}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">{t('allStatuses')}</option>
            <option value="unpaid">{t('unpaid')}</option>
            <option value="partially-paid">{t('partiallyPaid')}</option>
            <option value="paid">{t('paidLabel')}</option>
          </select>
        </label>
        <label className={styles.filterGroup}>
          <span className={styles.filterLabel}>{t('period')}</span>
          <select
            className={styles.filterSelect}
            value={filterPeriod}
            onChange={(e) => applyPeriod(e.target.value)}
          >
            <option value="daily">{t('daily')}</option>
            <option value="weekly">{t('weekly')}</option>
            <option value="monthly">{t('monthly')}</option>
            <option value="yearly">{t('yearly')}</option>
          </select>
        </label>
        <label className={styles.filterGroup}>
          <span className={styles.filterLabel}>{t('filterFrom')}</span>
          <input
            className={styles.filterInput}
            type="date"
            value={filterFrom}
            onChange={(e) => setFilterFrom(e.target.value)}
          />
        </label>
        <label className={styles.filterGroup}>
          <span className={styles.filterLabel}>{t('filterTo')}</span>
          <input
            className={styles.filterInput}
            type="date"
            value={filterTo}
            onChange={(e) => setFilterTo(e.target.value)}
          />
        </label>
        <label className={styles.filterCheckbox}>
          <input
            type="checkbox"
            checked={filterOverdueOnly}
            onChange={(e) => setFilterOverdueOnly(e.target.checked)}
          />
          <span>{t('showOverdueOnly')}</span>
        </label>
        <div className={styles.filterClearGroup}>
          <button
            className={styles.clearBtn}
            onClick={clearFilters}
            disabled={!hasFilters}
          >
            {t('clearFilters')}
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className={styles.loadingOverlay}><div className={styles.spinner} /></div>
      ) : error ? (
        <div className={styles.errorBanner}>{error}</div>
      ) : filteredDeals.length === 0 ? (
        <div className={styles.emptyState}>{hasFilters ? t('noDealsFiltered') : t('noDeals')}</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>{t('customer')}</th>
                <th>{t('salesRep')}</th>
                <th>{t('date')}</th>
                <th>{t('beforeTaxShort')}</th>
                <th>{t('tax')}</th>
                <th>{t('afterTax')}</th>
                <th>{t('paid')}</th>
                <th>{t('remaining')}</th>
                <th>{t('status')}</th>
                <th>{t('invoiceType')}</th>
                <th>{t('dueDate')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map((row, index) => {
                const { deal } = row;
                const dealId = String(deal._id);
                const isExpanded = expanded.has(dealId);
                const amountDue = getAmountDue(deal);
                const remaining = getRemaining(deal);
                const isFullyPaid = deal.paymentStatus === 'paid';
                const hasTaxData = deal.amountBeforeTax > 0;
                const isToggling = taxToggling === dealId;
                const taxLocked = (deal.paidAmount || 0) > 0;

                return (
                  <React.Fragment key={dealId}>
                    <tr className={styles.dealRow}>
                      <td>{index + 1}</td>
                      <td>
                        <div className={styles.customerCell}>
                          <span className={styles.customerName}>{row.customerName}</span>
                          <span className={styles.customerId}>{row.customerID}</span>
                        </div>
                      </td>
                      <td>{row.salesUserName || '—'}</td>
                      <td>{fmtDate(getDealDate(deal))}</td>
                      <td>{hasTaxData ? fmt(deal.amountBeforeTax) : '—'}</td>
                      <td>
                        {hasTaxData ? (
                          <span className={styles.taxCell}>
                            {fmt(deal.taxAmount)}
                            <span className={styles.taxRate}>({deal.taxRateSnapshot}%)</span>
                          </span>
                        ) : '—'}
                      </td>
                      <td>{hasTaxData ? fmt(deal.amountAfterTax) : '—'}</td>
                      <td className={styles.paidCell}>{fmt(deal.paidAmount || 0)}</td>
                      <td className={remaining > 0 ? styles.remainingCell : styles.paidCell}>
                        {hasTaxData ? fmt(remaining) : '—'}
                      </td>
                      <td>
                        <span className={`${styles.badge} ${paymentStatusClass[deal.paymentStatus] || ''}`}>
                          {statusLabel(deal.paymentStatus)}
                        </span>
                      </td>
                      <td>
                        <span className={styles.invoiceType}>
                          {deal.paymentType === 'postpaid' ? t('postpaid') : t('cash')}
                        </span>
                      </td>
                      <td className={isOverduePostpaid(deal) ? styles.remainingCell : undefined}>
                        {deal.paymentType === 'postpaid' ? fmtDate(deal.paymentDueDate) : '—'}
                      </td>
                      <td>
                        <div className={styles.actionsCell}>
                          {/* Tax inclusive toggle */}
                          {canChangeTaxMode && hasTaxData && !taxLocked && (
                            <button
                              className={`${styles.taxToggleBtn} ${deal.taxInclusive ? styles.taxToggleActive : ''}`}
                              title={taxLocked ? t('taxLocked') : (deal.taxInclusive ? t('collectWithTax') : t('collectWithoutTax'))}
                              disabled={isToggling || taxLocked}
                              onClick={() => handleTaxToggle(row, !deal.taxInclusive)}
                            >
                              {deal.taxInclusive ? 'Tax ✓' : 'No Tax'}
                            </button>
                          )}
                          {canChangeTaxMode && hasTaxData && taxLocked && (
                            <span className={styles.taxLockedBadge} title={t('taxLocked')}>
                              {deal.taxInclusive ? 'Tax ✓' : 'No Tax'}
                            </span>
                          )}
                          {/* Record payment */}
                          {canRecordPayments && !isFullyPaid && hasTaxData && (
                            <button
                              className={styles.payBtn}
                              onClick={() => openPayModal(row)}
                            >
                              {t('recordPayment')}
                            </button>
                          )}
                          {/* Payment history */}
                          <button
                            className={styles.historyBtn}
                            onClick={() => toggleExpand(dealId)}
                          >
                            {isExpanded ? t('hideHistory') : t('showHistory')}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Payment history expanded row */}
                    {isExpanded && (
                      <tr className={styles.historyRow}>
                        <td colSpan={13}>
                          <div className={styles.historyPanel}>
                            <div className={styles.historyTitle}>{t('paymentHistory')}</div>
                            {/* Financial breakdown */}
                            {hasTaxData && (
                              <div className={styles.breakdown}>
                                <div><span>{t('subtotal')}:</span><strong>{fmt(deal.subtotal)}</strong></div>
                                <div><span>{t('discount')} ({deal.dealDiscount || deal.requestedDiscountPercent || 0}%):</span><strong>−{fmt(deal.discountAmount)}</strong></div>
                                <div><span>{t('beforeTaxShort')}:</span><strong>{fmt(deal.amountBeforeTax)}</strong></div>
                                <div><span>{t('tax')} ({deal.taxRateSnapshot}%):</span><strong>{fmt(deal.taxAmount)}</strong></div>
                                <div className={styles.breakdownTotal}><span>{t('afterTax')}:</span><strong>{fmt(deal.amountAfterTax)}</strong></div>
                                <div><span>{t('amountDueLabel')} ({deal.taxInclusive ? t('withTax') : t('beforeTax')}):</span><strong>{fmt(amountDue)}</strong></div>
                              </div>
                            )}
                            <div className={styles.productsPanel}>
                              <div className={styles.historyTitle}>{t('products')}</div>
                              <table className={styles.historyTable}>
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>{t('product')}</th>
                                    <th>{t('sort')}</th>
                                    <th>{t('color')}</th>
                                    <th>{t('quantity')}</th>
                                    <th>{t('unitPrice')}</th>
                                    <th>{t('lineTotal')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(deal.products || []).map((product, index) => (
                                    <tr key={`${dealId}-product-${index}`}>
                                      <td>{index + 1}</td>
                                      <td>{product.productName || product.SKU}</td>
                                      <td>{product.sort || '—'}</td>
                                      <td>{product.color || '—'}</td>
                                      <td>{fmt(product.quantity || 0)}</td>
                                      <td>{fmt(product.pricePerItem || 0)}</td>
                                      <td>{fmt(getLineTotal(product))}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {/* Payment records */}
                            {!deal.payments?.length ? (
                              <p className={styles.noPayments}>{t('noPayments')}</p>
                            ) : (
                              <table className={styles.historyTable}>
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>{t('historyDate')}</th>
                                    <th>{t('historyAmount')}</th>
                                    <th>{t('historyMethod')}</th>
                                    <th>{t('historyBy')}</th>
                                    <th>{t('historyNotes')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {deal.payments.map((p, i) => (
                                    <tr key={i}>
                                      <td>{i + 1}</td>
                                      <td>{fmtDate(p.paidAt)}</td>
                                      <td className={styles.paidCell}>{fmt(p.amount)}</td>
                                      <td>{methodLabel(p.method)}</td>
                                      <td>{p.recordedByName || '—'}</td>
                                      <td>{p.notes || '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Record Payment Modal */}
      {payModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <h3 className={styles.modalTitle}>{t('recordPayment')}</h3>
            <div className={styles.modalMeta}>
              <span>{payModal.customerName} · {payModal.customerID}</span>
              <span>{t('amountDueLabel')}: <strong>{fmt(modalAmountDue)}</strong></span>
              <span>{t('remainingLabel')}: <strong>{fmt(modalRemaining)}</strong></span>
            </div>

            <label className={styles.modalLabel}>
              {t('amount')}
              <input
                className={styles.modalInput}
                type="number"
                min="0.01"
                step="0.01"
                max={modalRemaining || undefined}
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder={`Max ${fmt(modalRemaining)}`}
              />
            </label>

            <label className={styles.modalLabel}>
              {t('method')}
              <select
                className={styles.modalInput}
                value={payMethod}
                onChange={(e) => setPayMethod(e.target.value)}
              >
                <option value="cash">{t('cash')}</option>
                <option value="bank_transfer">{t('bankTransfer')}</option>
                <option value="cheque">{t('cheque')}</option>
                <option value="other">{t('other')}</option>
              </select>
            </label>

            <label className={styles.modalLabel}>
              {t('notes')}
              <textarea
                className={`${styles.modalInput} ${styles.modalTextarea}`}
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                rows={3}
              />
            </label>

            <div className={styles.modalActions}>
              <button className={styles.cancelBtn} onClick={closePayModal} disabled={payLoading}>
                {t('cancel')}
              </button>
              <button className={styles.confirmBtn} onClick={handleRecordPayment} disabled={payLoading}>
                {payLoading ? '…' : t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default DealsRegister;
